import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bell, BellOff, BellRing, Trash2, TrendingUp, TrendingDown, CheckCircle, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage } from '../i18n/LanguageContext';
import { cryptoApi } from '../utils/api';
import api from '../utils/api';
import { getPushStatus, enablePush, disablePush, pushSupported } from '../utils/push';
import Header from '../components/Header';
import CurrencyIcon from '../components/CurrencyIcon';
import { FadeUp } from '../components/Motion';

const ALERT_CRYPTOS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'DOGE', 'LTC', 'LINK', 'AVAX'];

const PriceAlerts = () => {
  const { t } = useLanguage();
  const a = t.alerts || {};
  const [rates, setRates] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [pushStatus, setPushStatus] = useState({ supported: true, subscribed: false, permission: 'default' });
  const [symbol, setSymbol] = useState('BTC');
  const [direction, setDirection] = useState('above');
  const [currency, setCurrency] = useState('eur');
  const [targetPrice, setTargetPrice] = useState('');
  const [creating, setCreating] = useState(false);

  const loadAlerts = useCallback(async () => {
    try { const r = await api.get('/alerts'); setAlerts(r.data.alerts || []); } catch {}
  }, []);

  useEffect(() => {
    loadAlerts();
    getPushStatus().then(setPushStatus);
    const fetchRates = async () => { try { const r = await cryptoApi.getRates(); setRates(r.data); } catch {} };
    fetchRates();
    const iv = setInterval(() => { fetchRates(); loadAlerts(); }, 30000);
    return () => clearInterval(iv);
  }, [loadAlerts]);

  const togglePush = async () => {
    try {
      if (pushStatus.subscribed) {
        await disablePush();
        toast.success(a.notifsDisabled || 'Notifications désactivées');
      } else {
        await enablePush();
        toast.success(a.notifsEnabled || 'Notifications activées !');
      }
    } catch (e) {
      if (e.message === 'denied') toast.error(a.notifsBlocked || 'Notifications bloquées par le navigateur. Autorisez-les dans les paramètres du site.');
      else if (e.message === 'unsupported') toast.error(a.notSupported || 'Navigateur non compatible');
      else toast.error(a.error || 'Une erreur est survenue');
    }
    setPushStatus(await getPushStatus());
  };

  const createAlert = async (e) => {
    e.preventDefault();
    const price = parseFloat(targetPrice);
    if (!price || price <= 0) { toast.error(a.invalidPrice || 'Prix cible invalide'); return; }
    setCreating(true);
    try {
      await api.post('/alerts', { symbol, direction, target_price: price, currency });
      toast.success(a.created || 'Alerte créée !');
      setTargetPrice('');
      loadAlerts();
      if (!pushStatus.subscribed) setTimeout(() => toast(a.enableHint || 'Activez les notifications pour être alerté même en arrière-plan', { icon: '💡' }), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || a.error || 'Une erreur est survenue');
    } finally { setCreating(false); }
  };

  const deleteAlert = async (id) => {
    try { await api.delete(`/alerts/${id}`); setAlerts(alerts.filter((al) => al.id !== id)); toast.success(a.deleted || 'Alerte supprimée'); } catch {}
  };

  const fmt = (v, cur) => `${v.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${cur === 'eur' ? '€' : '$'}`;
  const currentPrice = rates[symbol]?.[currency] || 0;
  const activeAlerts = alerts.filter((al) => al.status === 'active');
  const triggeredAlerts = alerts.filter((al) => al.status === 'triggered');

  return (
    <div className="min-h-screen bg-[#F5F6F7]" data-testid="price-alerts-page">
      <Header />

      <div className="bg-black px-6 pt-10 pb-24">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white/10 text-white/70 px-3 py-1.5 rounded-full text-xs font-bold uppercase mb-4">
                <BellRing className="w-3.5 h-3.5" /><span>{a.badge || 'Temps réel'}</span>
              </div>
              <h1 className="text-white font-extrabold text-4xl sm:text-5xl tracking-tight" style={{ fontFamily: 'Manrope' }}>
                {a.title || 'Alertes de prix'}
              </h1>
              <p className="text-white/50 text-base md:text-lg mt-3 max-w-xl">
                {a.subtitle || 'Recevez une notification dès que le prix atteint votre seuil, même si l’onglet est fermé.'}
              </p>
            </div>
            <button onClick={togglePush} data-testid="push-toggle-btn"
              className={`flex items-center space-x-2 px-6 py-3 rounded-full text-sm font-bold transition-colors ${
                pushStatus.subscribed ? 'bg-emerald-400/20 text-emerald-300 hover:bg-emerald-400/30' : 'btn-pill-white'
              }`}>
              {pushStatus.subscribed ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
              <span>{pushStatus.subscribed ? (a.disableNotifs || 'Désactiver les notifications') : (a.enableNotifs || 'Activer les notifications')}</span>
            </button>
          </motion.div>
        </div>
      </div>

      <div className="px-6 pb-16 -mt-14">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Create alert form */}
          <FadeUp className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)] h-fit" data-testid="create-alert-card">
            <h2 className="text-lg font-extrabold text-gray-900 mb-5 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              {a.createTitle || 'Nouvelle alerte'}
            </h2>
            <form onSubmit={createAlert} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">{a.crypto || 'Crypto'}</label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {ALERT_CRYPTOS.map((c) => (
                    <button type="button" key={c} onClick={() => setSymbol(c)} data-testid={`alert-crypto-${c}`}
                      className={`flex flex-col items-center py-2 rounded-2xl border transition-colors ${
                        symbol === c ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                      }`}>
                      <CurrencyIcon symbol={c} size={20} />
                      <span className="text-[10px] font-bold mt-1">{c}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">{a.condition || 'Condition'}</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button type="button" onClick={() => setDirection('above')} data-testid="alert-direction-above"
                    className={`flex items-center justify-center space-x-2 py-3 rounded-2xl text-sm font-bold border transition-colors ${
                      direction === 'above' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600'
                    }`}>
                    <TrendingUp className="w-4 h-4" /><span>{a.above || 'Au-dessus de'}</span>
                  </button>
                  <button type="button" onClick={() => setDirection('below')} data-testid="alert-direction-below"
                    className={`flex items-center justify-center space-x-2 py-3 rounded-2xl text-sm font-bold border transition-colors ${
                      direction === 'below' ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-gray-200 text-gray-600'
                    }`}>
                    <TrendingDown className="w-4 h-4" /><span>{a.below || 'En dessous de'}</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">{a.targetPrice || 'Prix cible'}</label>
                <div className="flex items-center space-x-2 mt-2">
                  <input type="number" step="any" min="0" value={targetPrice} onChange={(e) => setTargetPrice(e.target.value)}
                    placeholder={currentPrice ? currentPrice.toFixed(2) : '0.00'} data-testid="alert-target-price-input"
                    className="flex-1 px-4 py-3 rounded-2xl border border-gray-200 text-gray-900 font-mono-data font-bold focus:outline-none focus:border-gray-900 bg-white" />
                  <div className="flex rounded-2xl border border-gray-200 overflow-hidden">
                    {['eur', 'usd'].map((cur) => (
                      <button type="button" key={cur} onClick={() => setCurrency(cur)} data-testid={`alert-currency-${cur}`}
                        className={`px-4 py-3 text-sm font-bold transition-colors ${currency === cur ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}>
                        {cur === 'eur' ? '€' : '$'}
                      </button>
                    ))}
                  </div>
                </div>
                {currentPrice > 0 && (
                  <p className="text-xs text-gray-500 mt-2" data-testid="alert-current-price">
                    {a.currentPrice || 'Prix actuel'} : <span className="font-bold text-gray-900 font-mono-data">{fmt(currentPrice, currency)}</span>
                  </p>
                )}
              </div>

              <button type="submit" disabled={creating} data-testid="create-alert-btn"
                className="w-full btn-pill-dark py-3.5 flex items-center justify-center space-x-2 disabled:opacity-50">
                <Plus className="w-4 h-4" /><span>{creating ? (a.creating || 'Création...') : (a.create || "Créer l'alerte")}</span>
              </button>
            </form>
          </FadeUp>

          {/* Alerts lists */}
          <div className="lg:col-span-3 space-y-6">
            <FadeUp delay={0.1} className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="active-alerts-card">
              <h2 className="text-lg font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                {a.activeTitle || 'Alertes actives'} ({activeAlerts.length})
              </h2>
              {activeAlerts.length === 0 ? (
                <div className="text-center py-10">
                  <Bell className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">{a.noActive || 'Aucune alerte active. Créez-en une pour être notifié.'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeAlerts.map((al) => (
                    <div key={al.id} className="flex items-center justify-between p-4 rounded-2xl bg-gray-50" data-testid={`alert-row-${al.id}`}>
                      <div className="flex items-center space-x-3">
                        <CurrencyIcon symbol={al.symbol} size={36} />
                        <div>
                          <div className="font-bold text-gray-900 text-sm">{al.symbol}</div>
                          <div className={`text-xs font-semibold flex items-center space-x-1 ${al.direction === 'above' ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {al.direction === 'above' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            <span>{al.direction === 'above' ? (a.above || 'Au-dessus de') : (a.below || 'En dessous de')} {fmt(al.target_price, al.currency)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-right hidden sm:block">
                          <div className="text-xs text-gray-500">{a.currentPrice || 'Prix actuel'}</div>
                          <div className="text-sm font-bold text-gray-900 font-mono-data">{fmt(rates[al.symbol]?.[al.currency] || 0, al.currency)}</div>
                        </div>
                        <button onClick={() => deleteAlert(al.id)} data-testid={`delete-alert-${al.id}`}
                          className="p-2.5 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FadeUp>

            {triggeredAlerts.length > 0 && (
              <FadeUp delay={0.15} className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="triggered-alerts-card">
                <h2 className="text-lg font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                  {a.triggeredTitle || 'Alertes déclenchées'}
                </h2>
                <div className="space-y-2">
                  {triggeredAlerts.map((al) => (
                    <div key={al.id} className="flex items-center justify-between p-4 rounded-2xl bg-emerald-50/60" data-testid={`triggered-row-${al.id}`}>
                      <div className="flex items-center space-x-3">
                        <CurrencyIcon symbol={al.symbol} size={32} />
                        <div>
                          <div className="font-bold text-gray-900 text-sm flex items-center space-x-1.5">
                            <span>{al.symbol}</span><CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                          <div className="text-xs text-gray-600">
                            {a.triggeredAt || 'Déclenchée à'} {fmt(al.triggered_price || al.target_price, al.currency)}
                            {al.triggered_at && ` — ${new Date(al.triggered_at).toLocaleString('fr-FR')}`}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => deleteAlert(al.id)} data-testid={`delete-alert-${al.id}`}
                        className="p-2.5 rounded-full text-gray-500 hover:bg-gray-100 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </FadeUp>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceAlerts;
